// @ts-nocheck
export const LAMPORTS_PER_SOL = 1_000_000_000n;
export const TOKEN_DECIMALS = 1_000_000n;
export const PUMP_INITIAL_VIRTUAL_SOL_LAMPORTS = 30_000_000_000n;
export const PUMP_INITIAL_VIRTUAL_TOKEN_RAW = 1_073_000_000_000_000n;
export const PUMP_INITIAL_REAL_TOKEN_RAW = 793_100_000_000_000n;
export const PUMP_TOTAL_SUPPLY_RAW = 1_000_000_000_000_000n;
export const PUMP_MIGRATION_AMM_BASE_RAW = 200_000_000_000_000n;
export const PUMP_COMPLETION_VIRTUAL_TOKEN_RAW =
  PUMP_INITIAL_VIRTUAL_TOKEN_RAW - PUMP_INITIAL_REAL_TOKEN_RAW;
export const PUMP_INITIAL_MARKET_CAP_SOL =
  Number(PUMP_INITIAL_VIRTUAL_SOL_LAMPORTS * PUMP_TOTAL_SUPPLY_RAW / PUMP_INITIAL_VIRTUAL_TOKEN_RAW) /
  Number(LAMPORTS_PER_SOL);
export const PUMP_GRADUATION_VIRTUAL_SOL_LAMPORTS =
  (PUMP_INITIAL_VIRTUAL_SOL_LAMPORTS * PUMP_INITIAL_VIRTUAL_TOKEN_RAW) /
  PUMP_COMPLETION_VIRTUAL_TOKEN_RAW;
export const PUMP_GRADUATION_MARKET_CAP_SOL =
  Number(PUMP_GRADUATION_VIRTUAL_SOL_LAMPORTS * PUMP_TOTAL_SUPPLY_RAW / PUMP_COMPLETION_VIRTUAL_TOKEN_RAW) /
  Number(LAMPORTS_PER_SOL);

export type PumpAmmPoolState = {
  baseReserveRaw: bigint;
  quoteReserveRaw: bigint;
};

export type PumpBondingState = {
  virtualSolLamports: bigint;
  virtualTokenRaw: bigint;
  realSolLamports: bigint;
  realTokenRaw: bigint;
  tokenTotalSupplyRaw: bigint;
  complete: boolean;
  ammPool?: PumpAmmPoolState;
};

export type SimTrade = {
  ts: number;
  side: "buy" | "sell";
  sol?: number;
  tokenAmount?: number;
};

export type SimPrint = {
  ts: number;
  side: "buy" | "sell";
  walletSol: number;
  sol: number;
  curveSol?: number;
  protocolFeeSol?: number;
  creatorFeeSol?: number;
  jitoTipSol?: number;
  tokenAmount: number;
  marketCapSol: number;
  marketCapUsd: number;
  state: PumpBondingState;
  label?: string; // trader/wallet name for the live tape
};

export type SimCandle = {
  time: number;
  openSol?: number;
  highSol?: number;
  lowSol?: number;
  closeSol?: number;
  open: number;
  high: number;
  low: number;
  close: number;
  volumeSol: number;
  trades: number;
  buys: number;
  sells: number;
};

export type FeeBps = {
  protocolFeeBps: bigint;
  creatorFeeBps: bigint;
};

export type PumpTradeIntent = {
  side: "buy" | "sell";
  /**
   * For buys, this is either the wallet max spend or the exact curve/pool quote,
   * depending on buySolMode. For sells with tokenAmount, it is ignored.
   */
  sol?: number;
  /** UI token amount for exact buys/sells. */
  tokenAmount?: number;
  buySolMode?: "wallet-gross" | "curve-net";
  protocolFeeBps?: number;
  creatorFeeBps?: number;
  slippageBps?: number;
};

export type PumpIntentExecution = {
  next: PumpBondingState;
  side: "buy" | "sell";
  curveSolLamports: bigint;
  walletSolLamports: bigint;
  tokenRaw: bigint;
  protocolFeeLamports: bigint;
  creatorFeeLamports: bigint;
  maxQuoteLamports?: bigint;
  minTokenOutRaw?: bigint;
};

function ceilDiv(a: bigint, b: bigint): bigint {
  if (b <= 0n) throw new Error("division by zero");
  return (a + b - 1n) / b;
}

export function solToLamports(sol: number): bigint {
  if (!Number.isFinite(sol) || sol <= 0) return 0n;
  return BigInt(Math.round(sol * Number(LAMPORTS_PER_SOL)));
}

export function tokenUiToRaw(tokens: number): bigint {
  if (!Number.isFinite(tokens) || tokens <= 0) return 0n;
  return BigInt(Math.round(tokens * Number(TOKEN_DECIMALS)));
}

export function lamportsToSol(lamports: bigint): number {
  return Number(lamports) / Number(LAMPORTS_PER_SOL);
}

export function rawToTokenUi(raw: bigint): number {
  return Number(raw) / Number(TOKEN_DECIMALS);
}

export function createInitialPumpState(): PumpBondingState {
  return {
    virtualSolLamports: PUMP_INITIAL_VIRTUAL_SOL_LAMPORTS,
    virtualTokenRaw: PUMP_INITIAL_VIRTUAL_TOKEN_RAW,
    realSolLamports: 0n,
    realTokenRaw: PUMP_INITIAL_REAL_TOKEN_RAW,
    tokenTotalSupplyRaw: PUMP_TOTAL_SUPPLY_RAW,
    complete: false,
  };
}

export function clonePumpState(state: PumpBondingState): PumpBondingState {
  return { ...state, ammPool: state.ammPool ? { ...state.ammPool } : undefined };
}

export function marketCapSolFromState(state: PumpBondingState): number {
  if (state.ammPool && state.ammPool.baseReserveRaw > 0n) {
    const marketCapLamports =
      (state.ammPool.quoteReserveRaw * state.tokenTotalSupplyRaw) / state.ammPool.baseReserveRaw;
    return lamportsToSol(marketCapLamports);
  }
  if (state.virtualTokenRaw <= 0n) return 0;
  const marketCapLamports =
    (state.virtualSolLamports * state.tokenTotalSupplyRaw) / state.virtualTokenRaw;
  return lamportsToSol(marketCapLamports);
}

export function pumpTokensSoldRawFromState(state: PumpBondingState): bigint {
  const sold = PUMP_INITIAL_REAL_TOKEN_RAW - state.realTokenRaw;
  if (sold < 0n) return 0n;
  if (sold > PUMP_INITIAL_REAL_TOKEN_RAW) return PUMP_INITIAL_REAL_TOKEN_RAW;
  return sold;
}

export function pumpBondingProgressFromState(state: PumpBondingState): number {
  if (state.complete) return 1;
  return Number(pumpTokensSoldRawFromState(state)) / Number(PUMP_INITIAL_REAL_TOKEN_RAW);
}

export function pumpSolToGraduateFromState(state: PumpBondingState): number {
  if (state.complete) return 0;
  const left = PUMP_GRADUATION_VIRTUAL_SOL_LAMPORTS - state.virtualSolLamports;
  return left > 0n ? lamportsToSol(left) : 0;
}

function initializeAmmPoolFromMigration(state: PumpBondingState): PumpAmmPoolState {
  const quoteReserveRaw = state.realSolLamports > 0n ? state.realSolLamports : state.virtualSolLamports;
  return {
    baseReserveRaw: PUMP_MIGRATION_AMM_BASE_RAW,
    quoteReserveRaw,
  };
}

function ensureAmmState(state: PumpBondingState): PumpBondingState {
  if (!state.complete) return state;
  if (state.ammPool) return state;
  return { ...state, ammPool: initializeAmmPoolFromMigration(state) };
}

export function buyTokensForSolInput(state: PumpBondingState, solInLamports: bigint): bigint {
  if (solInLamports <= 0n || state.complete || state.virtualSolLamports <= 0n || state.virtualTokenRaw <= 0n) {
    return 0n;
  }
  const tokens = (solInLamports * state.virtualTokenRaw) / (state.virtualSolLamports + solInLamports);
  return tokens > state.realTokenRaw ? state.realTokenRaw : tokens;
}

export function pumpAmmBuyBaseOutForQuoteIn(
  pool: PumpAmmPoolState,
  quoteInRaw: bigint,
): bigint {
  if (quoteInRaw <= 0n || pool.baseReserveRaw <= 0n || pool.quoteReserveRaw <= 0n) return 0n;
  return (pool.baseReserveRaw * quoteInRaw) / (pool.quoteReserveRaw + quoteInRaw);
}

export function pumpAmmSellQuoteOutForBaseIn(
  pool: PumpAmmPoolState,
  baseInRaw: bigint,
): bigint {
  if (baseInRaw <= 0n || pool.baseReserveRaw <= 0n || pool.quoteReserveRaw <= 0n) return 0n;
  return (pool.quoteReserveRaw * baseInRaw) / (pool.baseReserveRaw + baseInRaw);
}

export function buySolCostForTokenOutput(state: PumpBondingState, tokenOutRaw: bigint): bigint {
  if (tokenOutRaw <= 0n || state.complete || state.virtualTokenRaw <= tokenOutRaw) return 0n;
  const capped = tokenOutRaw > state.realTokenRaw ? state.realTokenRaw : tokenOutRaw;
  return (capped * state.virtualSolLamports) / (state.virtualTokenRaw - capped) + 1n;
}

export function netSolBudgetFromGrossSpendLamports(
  spendLamports: bigint,
  feeBps: FeeBps,
): bigint {
  if (spendLamports <= 0n) return 0n;
  const denominator = 10_000n + feeBps.protocolFeeBps + feeBps.creatorFeeBps;
  let netSol = (spendLamports * 10_000n) / denominator;
  const protocolFee = ceilDiv(netSol * feeBps.protocolFeeBps, 10_000n);
  const creatorFee = ceilDiv(netSol * feeBps.creatorFeeBps, 10_000n);
  const total = netSol + protocolFee + creatorFee;
  if (total > spendLamports) netSol -= total - spendLamports;
  return netSol > 0n ? netSol : 0n;
}

export function feeLamportsForCurveAmount(amountLamports: bigint, feeBps: FeeBps): {
  protocolFeeLamports: bigint;
  creatorFeeLamports: bigint;
  totalFeeLamports: bigint;
} {
  const protocolFeeLamports = ceilDiv(amountLamports * feeBps.protocolFeeBps, 10_000n);
  const creatorFeeLamports = ceilDiv(amountLamports * feeBps.creatorFeeBps, 10_000n);
  return {
    protocolFeeLamports,
    creatorFeeLamports,
    totalFeeLamports: protocolFeeLamports + creatorFeeLamports,
  };
}

export function buyFromGrossSpend(
  state: PumpBondingState,
  spendLamports: bigint,
  feeBps: FeeBps,
): { curveSolLamports: bigint; tokenOutRaw: bigint } {
  const netBudget = netSolBudgetFromGrossSpendLamports(spendLamports, feeBps);
  const quoteSol = netBudget > 0n ? netBudget - 1n : 0n;
  const tokenOutRaw = buyTokensForSolInput(state, quoteSol);
  const curveSolLamports = buySolCostForTokenOutput(state, tokenOutRaw);
  return { curveSolLamports, tokenOutRaw };
}

export function executePumpTradeIntent(
  state: PumpBondingState,
  intent: PumpTradeIntent,
): PumpIntentExecution {
  const feeBps = {
    protocolFeeBps: BigInt(intent.protocolFeeBps ?? 95),
    creatorFeeBps: BigInt(intent.creatorFeeBps ?? 30),
  };
  const slippageBps = BigInt(Math.max(0, Math.round(intent.slippageBps ?? 0)));

  if (intent.side === "buy") {
    const requestedSolLamports = solToLamports(intent.sol ?? 0);
    if (!state.complete && intent.tokenAmount != null) {
      const tokenOutRaw = tokenUiToRaw(intent.tokenAmount);
      const curveSolLamports = buySolCostForTokenOutput(state, tokenOutRaw);
      const fees = feeLamportsForCurveAmount(curveSolLamports, feeBps);
      const maxQuoteLamports =
        slippageBps > 0n
          ? curveSolLamports + fees.totalFeeLamports + ceilDiv((curveSolLamports + fees.totalFeeLamports) * slippageBps, 10_000n)
          : undefined;
      const applied = applyPumpBuyByTokenOutput(state, tokenOutRaw);
      return {
        next: applied.next,
        side: "buy",
        curveSolLamports: applied.solInLamports,
        walletSolLamports: applied.solInLamports + fees.totalFeeLamports,
        tokenRaw: applied.tokenOutRaw,
        protocolFeeLamports: fees.protocolFeeLamports,
        creatorFeeLamports: fees.creatorFeeLamports,
        maxQuoteLamports,
      };
    }

    if (!state.complete && intent.buySolMode === "wallet-gross") {
      const quote = buyFromGrossSpend(state, requestedSolLamports, feeBps);
      const applied = applyPumpBuyByTokenOutput(state, quote.tokenOutRaw);
      const fees = feeLamportsForCurveAmount(applied.solInLamports, feeBps);
      return {
        next: applied.next,
        side: "buy",
        curveSolLamports: applied.solInLamports,
        walletSolLamports: applied.solInLamports + fees.totalFeeLamports,
        tokenRaw: applied.tokenOutRaw,
        protocolFeeLamports: fees.protocolFeeLamports,
        creatorFeeLamports: fees.creatorFeeLamports,
        maxQuoteLamports: requestedSolLamports,
      };
    }

    const applied = applyPumpBuyBySol(state, requestedSolLamports);
    const fees = state.complete ? { protocolFeeLamports: 0n, creatorFeeLamports: 0n, totalFeeLamports: 0n } : feeLamportsForCurveAmount(applied.solInLamports, feeBps);
    const minTokenOutRaw =
      slippageBps > 0n
        ? applied.tokenOutRaw - ((applied.tokenOutRaw * slippageBps) / 10_000n)
        : undefined;
    return {
      next: applied.next,
      side: "buy",
      curveSolLamports: applied.solInLamports,
      walletSolLamports: applied.solInLamports + fees.totalFeeLamports,
      tokenRaw: applied.tokenOutRaw,
      protocolFeeLamports: fees.protocolFeeLamports,
      creatorFeeLamports: fees.creatorFeeLamports,
      minTokenOutRaw,
    };
  }

  const tokenInRaw = tokenUiToRaw(intent.tokenAmount ?? 0);
  if (!state.complete) {
    const curveSolLamports = sellSolOutputForTokenInput(state, tokenInRaw);
    const fees = feeLamportsForCurveAmount(curveSolLamports, feeBps);
    const applied = applyPumpSellByTokens(state, tokenInRaw);
    return {
      next: applied.next,
      side: "sell",
      curveSolLamports: applied.solOutLamports,
      walletSolLamports: applied.solOutLamports > fees.totalFeeLamports ? applied.solOutLamports - fees.totalFeeLamports : 0n,
      tokenRaw: applied.tokenInRaw,
      protocolFeeLamports: fees.protocolFeeLamports,
      creatorFeeLamports: fees.creatorFeeLamports,
    };
  }

  const applied = applyPumpSellByTokens(state, tokenInRaw);
  return {
    next: applied.next,
    side: "sell",
    curveSolLamports: applied.solOutLamports,
    walletSolLamports: applied.solOutLamports,
    tokenRaw: applied.tokenInRaw,
    protocolFeeLamports: 0n,
    creatorFeeLamports: 0n,
  };
}

export function sellSolOutputForTokenInput(state: PumpBondingState, tokenInRaw: bigint): bigint {
  if (tokenInRaw <= 0n || state.virtualTokenRaw <= 0n || state.virtualSolLamports <= 0n) return 0n;
  return (tokenInRaw * state.virtualSolLamports) / (state.virtualTokenRaw + tokenInRaw);
}

export function applyPumpBuyBySol(state: PumpBondingState, solInLamports: bigint): {
  next: PumpBondingState;
  tokenOutRaw: bigint;
  solInLamports: bigint;
} {
  if (state.complete) {
    const next = ensureAmmState(clonePumpState(state));
    if (!next.ammPool) return { next, tokenOutRaw: 0n, solInLamports: 0n };
    const tokenOutRaw = pumpAmmBuyBaseOutForQuoteIn(next.ammPool, solInLamports);
    if (tokenOutRaw <= 0n) return { next, tokenOutRaw: 0n, solInLamports: 0n };
    next.ammPool.quoteReserveRaw += solInLamports;
    next.ammPool.baseReserveRaw -= tokenOutRaw;
    return { next, tokenOutRaw, solInLamports };
  }

  const next = clonePumpState(state);
  const tokenOutRaw = buyTokensForSolInput(next, solInLamports);
  if (tokenOutRaw <= 0n) return { next, tokenOutRaw: 0n, solInLamports: 0n };
  const curveSolLamports = buySolCostForTokenOutput(next, tokenOutRaw);

  next.virtualSolLamports += curveSolLamports;
  next.realSolLamports += curveSolLamports;
  next.virtualTokenRaw -= tokenOutRaw;
  next.realTokenRaw -= tokenOutRaw;
  if (next.realTokenRaw <= 0n) {
    next.realTokenRaw = 0n;
    next.complete = true;
  }
  return { next, tokenOutRaw, solInLamports: curveSolLamports };
}

export function applyPumpBuyByTokenOutput(state: PumpBondingState, tokenOutRaw: bigint): {
  next: PumpBondingState;
  tokenOutRaw: bigint;
  solInLamports: bigint;
} {
  if (state.complete) {
    return applyPumpBuyBySol(state, 0n);
  }

  const next = clonePumpState(state);
  const cappedTokenOutRaw = tokenOutRaw > next.realTokenRaw ? next.realTokenRaw : tokenOutRaw;
  const curveSolLamports = buySolCostForTokenOutput(next, cappedTokenOutRaw);
  if (cappedTokenOutRaw <= 0n || curveSolLamports <= 0n) {
    return { next, tokenOutRaw: 0n, solInLamports: 0n };
  }

  next.virtualSolLamports += curveSolLamports;
  next.realSolLamports += curveSolLamports;
  next.virtualTokenRaw -= cappedTokenOutRaw;
  next.realTokenRaw -= cappedTokenOutRaw;
  if (next.realTokenRaw <= 0n) {
    next.realTokenRaw = 0n;
    next.complete = true;
  }
  return { next, tokenOutRaw: cappedTokenOutRaw, solInLamports: curveSolLamports };
}

export function applyPumpBuyExactEvent(
  state: PumpBondingState,
  solInLamports: bigint,
  tokenOutRaw: bigint,
): {
  next: PumpBondingState;
  tokenOutRaw: bigint;
  solInLamports: bigint;
} {
  if (state.complete) {
    return applyPumpBuyBySol(state, solInLamports);
  }

  const next = clonePumpState(state);
  const cappedTokenOutRaw = tokenOutRaw > next.realTokenRaw ? next.realTokenRaw : tokenOutRaw;
  if (solInLamports <= 0n || cappedTokenOutRaw <= 0n) {
    return { next, tokenOutRaw: 0n, solInLamports: 0n };
  }

  next.virtualSolLamports += solInLamports;
  next.realSolLamports += solInLamports;
  next.virtualTokenRaw -= cappedTokenOutRaw;
  next.realTokenRaw -= cappedTokenOutRaw;
  if (next.realTokenRaw <= 0n) {
    next.realTokenRaw = 0n;
    next.complete = true;
  }
  return { next, tokenOutRaw: cappedTokenOutRaw, solInLamports };
}

export function applyPumpSellByTokens(state: PumpBondingState, tokenInRaw: bigint): {
  next: PumpBondingState;
  tokenInRaw: bigint;
  solOutLamports: bigint;
} {
  if (state.complete) {
    const next = ensureAmmState(clonePumpState(state));
    if (!next.ammPool) return { next, tokenInRaw: 0n, solOutLamports: 0n };
    const solOutLamports = pumpAmmSellQuoteOutForBaseIn(next.ammPool, tokenInRaw);
    if (solOutLamports <= 0n) return { next, tokenInRaw: 0n, solOutLamports: 0n };
    next.ammPool.baseReserveRaw += tokenInRaw;
    next.ammPool.quoteReserveRaw -= solOutLamports;
    return { next, tokenInRaw, solOutLamports };
  }

  const next = clonePumpState(state);
  const solOutLamports = sellSolOutputForTokenInput(next, tokenInRaw);
  if (solOutLamports <= 0n) return { next, tokenInRaw: 0n, solOutLamports: 0n };

  next.virtualSolLamports -= solOutLamports;
  next.realSolLamports -= solOutLamports;
  next.virtualTokenRaw += tokenInRaw;
  next.realTokenRaw += tokenInRaw;
  return { next, tokenInRaw, solOutLamports };
}

export function applyPumpSellExactEvent(
  state: PumpBondingState,
  tokenInRaw: bigint,
  solOutLamports: bigint,
): {
  next: PumpBondingState;
  tokenInRaw: bigint;
  solOutLamports: bigint;
} {
  if (state.complete) {
    return applyPumpSellByTokens(state, tokenInRaw);
  }

  const next = clonePumpState(state);
  if (tokenInRaw <= 0n || solOutLamports <= 0n) {
    return { next, tokenInRaw: 0n, solOutLamports: 0n };
  }

  next.virtualSolLamports -= solOutLamports;
  next.realSolLamports -= solOutLamports;
  next.virtualTokenRaw += tokenInRaw;
  next.realTokenRaw += tokenInRaw;
  return { next, tokenInRaw, solOutLamports };
}

export function replayPumpBondingTape(
  trades: SimTrade[],
  options: {
    solUsd: number;
    initialState?: PumpBondingState;
    buySolMode?: "curve-net" | "wallet-gross";
    protocolFeeBps?: number;
    creatorFeeBps?: number;
  },
): SimPrint[] {
  let state = clonePumpState(options.initialState ?? createInitialPumpState());
  const prints: SimPrint[] = [];
  const feeBps = {
    protocolFeeBps: BigInt(options.protocolFeeBps ?? 95),
    creatorFeeBps: BigInt(options.creatorFeeBps ?? 30),
  };

  for (const trade of trades) {
    if (trade.side === "buy") {
      const walletSol = trade.sol ?? 0;
      const solIn = solToLamports(walletSol);
      let applied;
      if (!state.complete && trade.tokenAmount != null) {
        applied = applyPumpBuyExactEvent(state, solIn, tokenUiToRaw(trade.tokenAmount));
      } else if (options.buySolMode === "wallet-gross" && !state.complete) {
        const quote = buyFromGrossSpend(state, solIn, feeBps);
        applied = applyPumpBuyByTokenOutput(state, quote.tokenOutRaw);
      } else {
        const curveBudget =
          options.buySolMode === "wallet-gross"
            ? netSolBudgetFromGrossSpendLamports(solIn, feeBps)
            : solIn;
        applied = applyPumpBuyBySol(state, curveBudget);
      }
      state = ensureAmmState(applied.next);
      const marketCapSol = marketCapSolFromState(state);
      prints.push({
        ts: trade.ts,
        side: "buy",
        walletSol,
        sol: lamportsToSol(applied.solInLamports),
        tokenAmount: rawToTokenUi(applied.tokenOutRaw),
        marketCapSol,
        marketCapUsd: marketCapSol * options.solUsd,
        state,
      });
    } else {
      const tokenIn = tokenUiToRaw(trade.tokenAmount ?? 0);
      const applied =
        !state.complete && trade.sol != null && trade.tokenAmount != null
          ? applyPumpSellExactEvent(state, tokenIn, solToLamports(trade.sol))
          : applyPumpSellByTokens(state, tokenIn);
      state = ensureAmmState(applied.next);
      const marketCapSol = marketCapSolFromState(state);
      prints.push({
        ts: trade.ts,
        side: "sell",
        walletSol: lamportsToSol(applied.solOutLamports),
        sol: lamportsToSol(applied.solOutLamports),
        tokenAmount: rawToTokenUi(applied.tokenInRaw),
        marketCapSol,
        marketCapUsd: marketCapSol * options.solUsd,
        state,
      });
    }
  }

  return prints;
}

export function candlesFromMarketCapPrints(prints: SimPrint[], bucketMs: number): SimCandle[] {
  const map = new Map<number, SimCandle>();
  const sorted = [...prints].sort((a, b) => a.ts - b.ts);
  let previousCloseSol: number | null = null;
  const solUsd = sorted[0]?.marketCapSol ? sorted[0]!.marketCapUsd / sorted[0]!.marketCapSol : 0;
  const initialOpenSol = PUMP_INITIAL_MARKET_CAP_SOL;

  for (const print of sorted) {
    const bucket = Math.floor(print.ts / bucketMs) * bucketMs;
    const existing = map.get(bucket);
    const mcSol = print.marketCapSol;
    const mcUsd = solUsd > 0 ? mcSol * solUsd : print.marketCapUsd;
    if (!existing) {
      const openSol = previousCloseSol ?? initialOpenSol ?? mcSol;
      const open = solUsd > 0 ? openSol * solUsd : print.marketCapUsd;
      map.set(bucket, {
        time: bucket,
        openSol,
        highSol: Math.max(openSol, mcSol),
        lowSol: Math.min(openSol, mcSol),
        closeSol: mcSol,
        open,
        high: Math.max(open, mcUsd),
        low: Math.min(open, mcUsd),
        close: mcUsd,
        volumeSol: print.sol,
        trades: 1,
        buys: print.side === "buy" ? 1 : 0,
        sells: print.side === "sell" ? 1 : 0,
      });
    } else {
      const openSol = existing.openSol ?? (solUsd > 0 ? existing.open / solUsd : existing.open);
      existing.openSol = openSol;
      existing.highSol = Math.max(existing.highSol ?? openSol, mcSol);
      existing.lowSol = Math.min(existing.lowSol ?? openSol, mcSol);
      existing.closeSol = mcSol;
      existing.high = Math.max(existing.high, mcUsd);
      existing.low = Math.min(existing.low, mcUsd);
      existing.close = mcUsd;
      existing.volumeSol += print.sol;
      existing.trades += 1;
      existing.buys += print.side === "buy" ? 1 : 0;
      existing.sells += print.side === "sell" ? 1 : 0;
    }
    previousCloseSol = mcSol;
  }

  return [...map.values()].sort((a, b) => a.time - b.time);
}
